import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { supabase } from '@/integrations/supabase/client';

export const PushNotificationPrompt = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { isSubscribed, loading, subscribe, isSupported, isConfigured } = usePushSubscription();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Only show prompt if user is logged in, not subscribed, and notifications are supported
    const dismissed = localStorage.getItem('push-prompt-dismissed');
    // Allow re-prompting after 7 days
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }
    if (isLoggedIn && !isSubscribed && isSupported) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, isSubscribed, isSupported]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Remember dismissal for 7 days
    localStorage.setItem('push-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already subscribed, not supported, or not configured
  if (!isVisible || isSubscribed || !isSupported || !isConfigured) {
    return null;
  }

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 max-w-sm animate-in slide-in-from-bottom-4">
      <Card className="shadow-lg border-primary/20">
        <CardHeader className="relative pb-3">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Stay Updated</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <CardDescription>
            Enable push notifications to get instant updates about:
          </CardDescription>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Session start reminders</li>
            <li>• New booking confirmations</li>
            <li>• Direct messages</li>
            <li>• Coach availability alerts</li>
          </ul>
          <div className="flex gap-2">
            <Button 
              onClick={handleEnable} 
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDismiss}
              className="flex-1"
            >
              Maybe Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};